import React from 'react';

import { Tooltip } from 'antd';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import ActionButton from '@src/components/Buttons/ActionButton';
import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import IntlMessages from '@src/util/IntlMessages';

import { LOW_BATTERY_THRESHOLD } from '../../constants';
import type { ActiveUserListItem, ExecutionComplianceType } from '../../types';
import { formatAbsoluteTime, formatLastSeen } from '../../utils';
import styles from './ActiveUserList.module.less';

interface ActiveUserListRowProps {
  user: ActiveUserListItem;
  isMobile: boolean;
  nowMs: number;
  timeZone: string;
  onFindUser: (user: ActiveUserListItem) => void;
  onTimeline: (userId: string) => void;
  onSchedules: (user: ActiveUserListItem) => void;
  onOpenCompliance: (user: ActiveUserListItem, type: ExecutionComplianceType) => void;
}

const batteryTone = (battery: number | null): string => {
  if (battery === null) return styles.batteryUnknown;
  if (battery <= LOW_BATTERY_THRESHOLD) return styles.batteryLow;
  if (battery < 50) return styles.batteryMedium;
  return styles.batteryOk;
};

const handleRowKeyDown = (
  event: React.KeyboardEvent<HTMLDivElement>,
  onActivate: () => void,
): void => {
  // Nested buttons handle their own keys; intercepting them would cancel their click.
  if (event.target !== event.currentTarget) return;
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onActivate();
  }
};

const ActiveUserListRow: React.FC<ActiveUserListRowProps> = ({
  user,
  isMobile,
  nowMs,
  timeZone,
  onFindUser,
  onTimeline,
  onSchedules,
  onOpenCompliance,
}) => {
  const intl = useIntl();
  const lastSeenLabel = formatLastSeen(intl, user.lastSeenAt, nowMs);
  const lastSeenAbsolute = formatAbsoluteTime(intl, user.lastSeenAt, timeZone);
  const handleRowActivate = () => onFindUser(user);

  const actions = (
    <div className={styles.actions} onClick={event => event.stopPropagation()} role="presentation">
      <ActionButton.Container
        maxVisibleButtons={0}
        overflowVariant="labeled"
        buttons={[
          <ActionButton
            key="locate"
            icon="my_location"
            tooltipText={intl.formatMessage({ id: user.positions
              ? 'activeUserDashboard.findUserTooltip'
              : 'activeUserDashboard.findUserDisconnectedTooltip' })}
            onClick={() => onFindUser(user)}
          />,
          <ActionButton
            key="timeline"
            icon="format_list_bulleted"
            tooltipText={intl.formatMessage({ id: 'activeUserDashboard.timelineTooltip' })}
            onClick={() => onTimeline(user.id)}
          />,
          <ActionButton
            key="schedules"
            icon="calendar_month"
            tooltipText={intl.formatMessage({ id: 'activeUserDashboard.schedule.inprogress.column' })}
            onClick={() => onSchedules(user)}
          />,
        ]}
      />
    </div>
  );

  const userCell = (
    <div className={styles.userCell}>
      <div className={styles.avatarWrap}>
        <MemberAvatar user={user} />
        <span
          className={classNames(styles.statusDot, user.connected ? styles.online : styles.offline)}
        />
        {user.delays && (
          <Tooltip title={<IntlMessages id="activeUserDashboard.filter.stalled" />}>
            <span className={styles.stalledDot} />
          </Tooltip>
        )}
      </div>
      <div className={styles.userMeta}>
        <span className={styles.userName}>{user.displayName}</span>
        <Tooltip title={lastSeenAbsolute}>
          <span className={styles.lastSeen}>{lastSeenLabel}</span>
        </Tooltip>
        {user.trackingDisplay !== 'unknown' && (
          <span className={styles.lastSeen}>
            <IntlMessages id={`activeUserDashboard.tracking.${user.trackingDisplay}`} />
            {!user.positionCurrent && user.positionTimeFormated
              ? ` · ${user.positionTimeFormated}`
              : ''}
          </span>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div
        className={classNames(styles.rowMobile, user.isSelected && styles.rowSelected)}
      >
        <button
          type="button"
          className={styles.mobileUser}
          onClick={handleRowActivate}
          aria-label={`${intl.formatMessage({ id: 'activeUserDashboard.findUserTooltip' })}: ${user.displayName}`}
        >
          {userCell}
        </button>
        <div className={styles.mobileRight}>
          <span className={classNames(styles.battery, batteryTone(user.battery))}>
            {user.battery !== null ? `${user.battery}%` : '—'}
          </span>
          {actions}
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.row, user.isSelected && styles.rowSelected)}
      onClick={handleRowActivate}
      onKeyDown={event => handleRowKeyDown(event, handleRowActivate)}
      role="button"
      tabIndex={0}
    >
      {userCell}
      <div className={styles.cell}>
        {user.eventsToday !== null ? user.eventsToday : <span className={styles.noData}>—</span>}
      </div>
      <div className={styles.cell}>
        <button type="button" className={styles.complianceLink} onClick={event => {
          event.stopPropagation();
          onOpenCompliance(user, 'schedule');
        }}>
          {user.scheduleCompliance[0]}/{user.scheduleCompliance[1]}
        </button>
      </div>
      <div className={styles.cell}>
        <button type="button" className={styles.complianceLink} onClick={event => {
          event.stopPropagation();
          onOpenCompliance(user, 'task');
        }}>
          {user.taskCompliance[0]}/{user.taskCompliance[1]}
        </button>
      </div>
      <div className={styles.cell}>
        <button type="button" className={styles.complianceLink} onClick={event => {
          event.stopPropagation();
          onOpenCompliance(user, 'checkpoint_route');
        }}>
          {user.checkpointRouteCompliance[0]}/{user.checkpointRouteCompliance[1]}
        </button>
      </div>
      <div className={classNames(styles.cell, styles.battery, batteryTone(user.battery))}>
        {user.battery !== null ? `${user.battery}%` : '—'}
      </div>
      <div className={styles.cell} role="presentation">
        {actions}
      </div>
    </div>
  );
};

export default React.memo(ActiveUserListRow);
